import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import { isFunction } from 'lodash';
import { fieldProps, getPreferredProp } from "../utils";
import { forwardRef } from 'react';
import { isKeyPressed } from "../../../utils/isKeyPressed";
import { RETURN_KEY_CODE, SPACEBAR_KEY_CODE } from "../../../utils/Browser/keyCodes";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const CheckBoxField = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    id,
    label,
    disabled = false,
    hideLabel = false,
    hint = '',
    labelPosition = 'right',
    showError = true,
    showHint = true,
    required = false,
    style = {
      container: '',
      label: '',
      input: '',
      hint: '',
      error: ''
    }
  } = props;
  const onChange = e => {
    if (!disabled) {
      document.getElementById(id)?.focus();
      const value = e.target.checked;
      const callback = getPreferredProp(props, 'onChange');
      if (isFunction(callback)) {
        callback(e);
      }
      if (isFunction(props.onChange) && props.onChange !== callback) {
        props.onChange(value);
      }
    }
  };
  const onKeyDown = e => {
    if (!disabled && (isKeyPressed(e, 'Enter', RETURN_KEY_CODE) || isKeyPressed(e, ' ', SPACEBAR_KEY_CODE))) {
      e.preventDefault();
      onChange({
        target: {
          checked: !isChecked
        }
      });
    }
  };
  const isChecked = !!(getPreferredProp(props, 'value') ?? getPreferredProp(props, 'checked'));
  const hasError = getPreferredProp(props, 'invalid') && getPreferredProp(props, 'touched');
  const error = getPreferredProp(props, 'error') || (required ? 'Required' : '');
  const inputLabelWrapperClass = cn('flex gap-2 items-center relative', {
    'flex-row-reverse': labelPosition === 'left',
    'content-end': labelPosition === 'left',
    'opacity-50': disabled
  });
  const inputClass = cn('appearance-none border border-solid w-4 h-4 focus:outline-solid-offset rounded relative flex-shrink-0', {
    'border-medium-grey': !hasError,
    'border-red': hasError,
    'checked:bg-action-blue checked:border-action-blue': isChecked,
    'hover:bg-medium-fill-grey': !disabled && !isChecked,
    'cursor-pointer': !disabled
  }, style.input);
  const labelClass = cn('text-sm text-text-blue font-regular-font tracking-normal normal-case', {
    'text-right': labelPosition === 'left',
    'sr-only': hideLabel,
    'cursor-pointer': !disabled
  }, style.label);
  const svgClass = cn('absolute w-4 h-4 rounded', {
    'hover:bg-deep-blue cursor-pointer': !disabled
  });
  const hintClass = cn('text-xs italic leading-4 text-medium-grey pt-1', {
    'opacity-50': disabled,
    'text-right': labelPosition === 'left'
  }, style.hint);
  const errorClass = cn('pt-1 text-red text-xs', {
    'text-right': labelPosition === 'left'
  }, style.error);
  return /*#__PURE__*/_jsx("div", {
    className: cn(style.container),
    id: `${id}-uu-checkbox-field__container`,
    "data-testid": `${id}-uu-checkbox-field__container`,
    children: /*#__PURE__*/_jsxs("div", {
      className: "inline-block align-middle",
      children: [/*#__PURE__*/_jsxs("div", {
        className: inputLabelWrapperClass,
        children: [/*#__PURE__*/_jsx("input", {
          ref: ref,
          id: id,
          "data-testid": `${id}-uu-checkbox-field__input`,
          type: "checkbox",
          checked: isChecked,
          className: inputClass,
          onChange: onChange,
          onBlur: getPreferredProp(props, 'onBlur'),
          onFocus: getPreferredProp(props, 'onFocus'),
          onKeyDown: onKeyDown,
          name: getPreferredProp(props, 'name'),
          disabled: disabled,
          "aria-label": label
        }), /*#__PURE__*/_jsx("label", {
          htmlFor: id,
          className: labelClass,
          id: `${id}-uu-checkbox-field__label`,
          "data-testid": `${id}-uu-checkbox-field__label`,
          children: label
        }), isChecked && /*#__PURE__*/_jsx("svg", {
          id: `${id}-uu-checkbox-field__check`,
          "data-testid": `${id}-uu-checkbox-field__check`,
          className: svgClass,
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "white",
          strokeWidth: "3",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          onClick: onChange,
          children: /*#__PURE__*/_jsx("polyline", {
            points: "20 6 9 17 4 12",
            fill: "none"
          })
        })]
      }), showHint && hint && /*#__PURE__*/_jsx("div", {
        className: hintClass,
        id: `${id}-uu-checkbox-field__hint`,
        "data-testid": `${id}-uu-checkbox-field__hint`,
        children: hint
      }), showError && hasError && /*#__PURE__*/_jsx("div", {
        className: errorClass,
        id: `${id}-uu-checkbox-field__error`,
        "data-testid": `${id}-uu-checkbox-field__error`,
        children: error
      })]
    })
  });
});
CheckBoxField.propTypes = {
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  style: PropTypes.shape({
    container: PropTypes.string,
    button: PropTypes.string,
    hint: PropTypes.string,
    label: PropTypes.string,
    toggle: PropTypes.string,
    wrapper: PropTypes.string
  }),
  hideLabel: PropTypes.bool,
  label: PropTypes.string.isRequired,
  labelPosition: PropTypes.oneOf(['right', 'left']),
  disabled: PropTypes.bool,
  hint: PropTypes.node,
  showError: PropTypes.bool,
  showHint: PropTypes.bool,
  required: PropTypes.bool,
  ...fieldProps
};
export default CheckBoxField;
//# sourceMappingURL=CheckBoxField.js.map